/**
 * FAQ Accordion JavaScript
 * 
 * Handles accordion functionality with accessibility support.
 *
 * @package CanadaAbroad
 */

(function() {
    'use strict';

    /**
     * Initialize all FAQ components on the page
     */
    function initFAQ() {
        const faqContainers = document.querySelectorAll('.ca-faq');
        
        faqContainers.forEach(function(container) {
            initFAQContainer(container);
        });
    }

    /**
     * Initialize a single FAQ container
     * @param {HTMLElement} container - The FAQ container element
     */
    function initFAQContainer(container) {
        const allowMultiple = container.dataset.allowMultiple === 'true';
        const triggers = container.querySelectorAll('[data-faq-trigger]');

        triggers.forEach(function(trigger) {
            trigger.addEventListener('click', function(e) {
                e.preventDefault();
                handleTriggerClick(trigger, container, allowMultiple);
            });

            // Keyboard support
            trigger.addEventListener('keydown', function(e) {
                handleKeydown(e, trigger, container, allowMultiple);
            });
        });
    }

    /**
     * Handle FAQ trigger click
     * @param {HTMLElement} trigger - The clicked trigger button
     * @param {HTMLElement} container - The FAQ container
     * @param {boolean} allowMultiple - Whether multiple items can be open
     */
    function handleTriggerClick(trigger, container, allowMultiple) {
        const item = trigger.closest('[data-faq-item]');
        const isOpen = item.classList.contains('ca-faq__item--open');

        // Close other items if not allowing multiple
        if (!allowMultiple && !isOpen) {
            closeAllItems(container);
        }

        // Toggle current item
        if (isOpen) {
            closeItem(item);
        } else {
            openItem(item);
        }
    }

    /**
     * Handle keyboard navigation
     * @param {KeyboardEvent} e - The keyboard event
     * @param {HTMLElement} trigger - The trigger button
     * @param {HTMLElement} container - The FAQ container
     * @param {boolean} allowMultiple - Whether multiple items can be open
     */
    function handleKeydown(e, trigger, container, allowMultiple) {
        const triggers = Array.from(container.querySelectorAll('[data-faq-trigger]'));
        const currentIndex = triggers.indexOf(trigger);

        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                handleTriggerClick(trigger, container, allowMultiple);
                break;
            
            case 'ArrowDown':
                e.preventDefault();
                if (currentIndex < triggers.length - 1) {
                    triggers[currentIndex + 1].focus();
                }
                break;
            
            case 'ArrowUp':
                e.preventDefault();
                if (currentIndex > 0) {
                    triggers[currentIndex - 1].focus();
                }
                break;
            
            case 'Home':
                e.preventDefault();
                triggers[0].focus();
                break;
            
            case 'End':
                e.preventDefault();
                triggers[triggers.length - 1].focus();
                break;
        }
    }

    /**
     * Open a FAQ item
     * @param {HTMLElement} item - The FAQ item to open
     */
    function openItem(item) {
        const trigger = item.querySelector('[data-faq-trigger]');
        const content = item.querySelector('[data-faq-content]');

        item.classList.add('ca-faq__item--open');
        trigger.setAttribute('aria-expanded', 'true');
        content.removeAttribute('hidden');

        // Smooth scroll into view if needed
        requestAnimationFrame(function() {
            const rect = item.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            
            if (rect.top < 0 || rect.bottom > windowHeight) {
                item.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest'
                });
            }
        });
    }

    /**
     * Close a FAQ item
     * @param {HTMLElement} item - The FAQ item to close
     */
    function closeItem(item) {
        const trigger = item.querySelector('[data-faq-trigger]');
        const content = item.querySelector('[data-faq-content]');

        item.classList.remove('ca-faq__item--open');
        trigger.setAttribute('aria-expanded', 'false');
        content.setAttribute('hidden', '');
    }

    /**
     * Close all FAQ items in a container
     * @param {HTMLElement} container - The FAQ container
     */
    function closeAllItems(container) {
        const openItems = container.querySelectorAll('.ca-faq__item--open');
        openItems.forEach(function(item) {
            closeItem(item);
        });
    }

    // Initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initFAQ);
    } else {
        initFAQ();
    }

    // Re-initialize on dynamic content load (for AJAX)
    window.initFAQ = initFAQ;

})();

